import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'

import { useMyBonusHistoryQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'

import styles from './styles.module.scss'

const orderIdFromPurchaseId = (purchaseId?: string | null): string | null => {
  if (!purchaseId) return null
  const parts = purchaseId.split(';')
  return parts.length > 1 ? parts[1] : purchaseId
}

const mapOperationType = (
  type?: string | null,
  comment?: string | null,
  purchaseId?: string | null,
  t?: (key: string) => string,
): React.ReactNode => {
  if (!type) return <>error</>
  if (comment) return <>{comment}</>

  const orderNum = orderIdFromPurchaseId(purchaseId)

  switch (type) {
  case 'charge':
    return (
      <>
        {t?.('bonusOperationType.charge')}
      </>
    )
  case 'chargePaymentReturn':
    return <>{t?.('bonusOperationType.chargePaymentReturn')}</>
  case 'activate':
    return (
      <>
        {t?.('bonusOperationType.activate')}
        {orderNum ? (
          <>
            {' '}
            <span className={styles.greenText}>№ {orderNum}</span>
          </>
        ) : null}
      </>
    )
  case 'cancel':
    return <>{t?.('bonusOperationType.cancel')}</>
  case 'block':
    return <>{t?.('bonusOperationType.block')}</>
  case 'unblock':
    return <>{t?.('bonusOperationType.unblock')}</>
  case 'writeOff':
    return (
      <>
        {t?.('bonusOperationType.writeOff')}
      </>
    )
  case 'writeOffByReturn':
    return <>{t?.('bonusOperationType.writeOffByReturn')}</>
  case 'writeOffBlocked':
    return <>{t?.('bonusOperationType.writeOffBlocked')}</>
  case 'expire':
    return <>{t?.('bonusOperationType.expire')}</>
  case 'prolong':
    return <>{t?.('bonusOperationType.prolong')}</>
  case 'unknown':
    return <>{t?.('bonusOperationType.unknown')}</>
  default:
    return <>{type}</>
  }
}

export const BonusList: React.FC = () => {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const { data: { bonusHistory } = {} } = useMyBonusHistoryQuery()
  const { t: t } = useTranslation('personal')

  const parsedBonuses =
    bonusHistory?.map((item) => {
      const isNegative = 
        item?.type === 'writeOff' || 
        item?.type === 'writeOffByReturn' || 
        item?.type === 'writeOffBlocked' || 
        item?.type === 'expire' ||
        item?.type === 'block'
      const sign = isNegative ? '-' : '+'
      const date = new Date(item?.operationAt).toLocaleDateString('ru-RU')
      const Operation = mapOperationType(
        item?.type,
        item?.comment,
        item?.purchaseId,
        t,
      )

      return {
        amount: `${sign} ${item?.amount || 0}`,
        isNegative,
        date,
        operation: Operation,
      }
    }) || []

  return (
    <div className={styles.list}>
      {!isMobile && (
        <div className={styles.list__head}>
          <span>{t('bonusHistory.bonuses')}</span>
          <span>{t('bonusHistory.date')}</span>
          <span>{t('bonusHistory.type')}</span>
        </div>
      )}
      <div className={styles.list__body}>
        <div className={styles.list__content}>
          {parsedBonuses.map((item, i) =>
            isMobile ? (
              <div className={styles.card} key={`${item.amount}-${i}`}>
                <div>
                  <p>{t('bonusHistory.bonuses')} </p>
                  <span
                    className={cn(
                      styles.card__amount,
                      item.isNegative ? styles.redText : styles.greenText,
                    )}
                  >
                    {' '}
                    {item.amount} {t('bonusHistory.bonusesAbbreviated')}
                  </span>
                </div>
                <div>
                  <p>{t('bonusHistory.date')} </p>
                  <span> {item.date}</span>
                </div>
                <div>
                  <p>{t('bonusHistory.type')} </p>
                  <span> {item.operation}:</span>
                </div>
              </div>
            ) : (
              <div className={styles.list__item} key={`${item.amount}-${i}`}>
                <div className={styles.list__amount}>
                  <p
                    className={
                      item.isNegative ? styles.redText : styles.greenText
                    }
                  >
                    {item.amount} {t('bonusHistory.bonusesAbbreviated')}
                  </p>
                  {/* <span>~12 ₸</span> */}
                </div>
                <span>{item.date}</span>
                <div className={styles.list__operation}>
                  <p>
                    {item.operation}
                  </p>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
